import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  getFAQSchema,
  getBreadcrumbSchema,
  defaultFAQs
} from '../utils/seoSchemas';

const FAQ = () => {
  const faqSchema = getFAQSchema(defaultFAQs);
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'FAQ', url: 'https://www.go-ops.digital/faq' }
  ]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>FAQ | Go Ops - AI Garage Management Software</title>
        <meta
          name="description"
          content="Frequently asked questions about Go Ops, the AI-powered garage management system for UK independent garages. Learn about features, pricing, and how it compares to other software."
        />
        <link rel="canonical" href="https://www.go-ops.digital/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="FAQ | Go Ops - AI Garage Management Software" />
        <meta
          property="og:description"
          content="Frequently asked questions about Go Ops, the AI-powered garage management system for UK independent garages."
        />
        <meta property="og:url" content="https://www.go-ops.digital/faq" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FAQ | Go Ops - AI Garage Management Software" />
        <meta
          name="twitter:description"
          content="Frequently asked questions about Go Ops, the AI-powered garage management system for UK independent garages."
        />
        <meta name="twitter:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="text"
                component={RouterLink}
                to="/about"
                sx={{ color: 'white' }}
              >
                About
              </Button>
              <Button
                variant="text"
                component={RouterLink}
                to="/faq"
                sx={{ color: 'white' }}
              >
                FAQ
              </Button>
              <Button
                variant="text"
                component={RouterLink}
                to="/contact"
                sx={{ color: 'white' }}
              >
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
            Everything you need to know about Go Ops garage management software
          </Typography>
        </Container>
      </Box>

      {/* FAQ Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        {defaultFAQs.map((faq, index) => (
          <Accordion
            key={index}
            sx={{
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: 'white',
                '&:hover': { bgcolor: '#f5f5f5' },
                '& .MuiAccordionSummary-content': { my: 2 }
              }}
            >
              <Typography variant="h3" component="h3" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: 'white', pt: 0, pb: 3 }}>
              <Typography sx={{ color: '#555', lineHeight: 1.7 }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Still have questions?
          </Typography>
          <Typography sx={{ mb: 3, color: '#666' }}>
            Get in touch with our team for a personalised demo and pricing.
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/contact"
            size="large"
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
          >
            Contact Us
          </Button>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/about" sx={{ color: 'white', textTransform: 'none' }}>
                About
              </Button>
              <Button component={RouterLink} to="/faq" sx={{ color: 'white', textTransform: 'none' }}>
                FAQ
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FAQ;
